import type { GameDriver } from './GameDriver.js';
import { LiveSocketDriver } from './LiveSocketDriver.js';
import { MockDriver } from './MockDriver.js';

export type { ConnectOptions, GameDriver } from './GameDriver.js';
export { LiveSocketDriver, wsBaseUrl } from './LiveSocketDriver.js';
export { MockDriver } from './MockDriver.js';
export { HOST_ID as MOCK_HOST_ID } from './mockFixtures.js';

/**
 * True when the app should run against the scripted in-browser MockDriver
 * instead of the live server. Opt in with `?mock=1` (or `#mock`) on any route —
 * offline demo insurance when there's no server / no network.
 */
export function useMockDriver(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get('mock') === '1' || params.has('mock')) return true;
  const hash = window.location.hash.toLowerCase();
  return hash.includes('mock');
}

/**
 * The single driver factory. Every surface (Host / Play) builds its driver here
 * so the Mock↔Live choice lives in ONE place.
 *
 * - default            → LiveSocketDriver (real ws to the game server)
 * - `?mock=1` / `#mock` → MockDriver (scripted, offline)
 *
 * `selfId` only matters for the Mock (which player the scripted hand represents);
 * the live server assigns identity, so it's ignored there.
 */
export function createDriver(selfId?: string): GameDriver {
  if (useMockDriver()) return new MockDriver(selfId);
  return new LiveSocketDriver();
}
